import InvoiceRecepients from "@dana/Components/OrderHistory/InvoiceRecepients";
import { PrintButton } from "@dana/Components/PrintButton/PrintButton";
import StandardReturnsOrderRecepients from "@dana/Components/StandardReturnsOrderStatus/StandardReturnsOrderRecepients";
import StyledTable from "@dana/Components/StyledTable/StyledTable";
import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontSizes, fontWeights, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import {
    OrderInfo,
    RmaAdditionalDetails,
    SalseOrgDetails,
    StandardReturnOrderDetails,
} from "@dana/Services/StandardReturnDetailsService";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import exportStandardReturnOrder from "@dana/Store/Data/StandardReturnsStatus/Handlers/ExportStandardReturnOrder";
import { SystemListValuesModel } from "@dana/Types/ApiModels";
import { formatDecimal } from "@dana/utils/utils";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import translate from "@insite/client-framework/Translate";
import Checkbox from "@insite/mobius/Checkbox";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// --- Props Interface ---
interface RMADetailsViewProps {
    // Define your props here
}

// --- Styles Interfaces and Objects ---
export interface RMADetailsViewStyles {
    invoiceContainer?: InjectableCss;
}

export const RMADetailsViewStyles: RMADetailsViewStyles = {
    invoiceContainer: {
        css: css`
            font-size: ${fontSizes.medium};
            line-height: ${lineHeights.medium};

            .invoice-actions {
                display: flex;
                align-items: center;
                justify-content: flex-end;
                gap: 16px;
                padding: 16px 0;
            }

            .border-box {
                border: 1px solid ${themeBackgrounds.lightGray};
                padding: 16px;
            }

            table {
                width: 100%;

                .price-cell {
                    text-align: right;
                }
            }

            td.company-logo {
                width: 200px;
            }

            td.reprint-text {
                width: 200px;
                text-align: right;
                vertical-align: top;
            }

            address {
                font-style: normal;
            }

            .invoice-address-table {
                .section-left {
                    width: 45%;
                    padding-right: 30px;
                }

                .section-right {
                    width: 55%;
                }
            }

            .delivery-information-table {
                .invoice_card {
                    height: fit-content;
                }
            }

            .reason-disposition-table {
                ul {
                    padding-left: 24px;
                    margin-top: 16px;
                }
            }

            .line-item-table-wrapper {
                overflow: auto;
            }

            .address-table {
                .invoice_card {
                    height: 150px;
                }
            }

            .company-address {
                h3 {
                    font-weight: ${fontWeights.semiBold};
                }
            }

            .printtype-text {
                font-size: ${fontSizes.large};
                line-height: ${lineHeights.large};
                font-weight: ${fontWeights.semiBold};
                color: ${themeColors.primary.danaBlue};
                text-transform: uppercase;
                margin-bottom: 16px;
            }

            .print-text {
                font-weight: ${fontWeights.semiBold};
                text-transform: uppercase;
            }

            .header-table {
                margin-bottom: 24px;
            }

            .freight-collect-information {
                page-break-before: always;
                margin-top: 32px;

                .info-title {
                    width: 350px;
                }
            }

            .break-spaces {
                white-space: break-spaces;
            }

            .pricing-box {
                > div {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    margin: 8px 0;
                }
            }

            .cutting-card-wrapper {
                font-size: 23px;
                font-weight: 600;
                max-width: 850px;
                margin: 32px auto;

                &.first-card {
                    page-break-before: always;
                }

                .cutting-card {
                    border: 2px dashed black;
                    padding: 8px;
                    margin-top: 35px;
                    position: relative;

                    &::before {
                        content: "✂";
                        position: absolute;
                        font-size: 60px;
                        top: -45px;
                        right: 0px;
                        transform: rotate(140deg);

                        @media only screen and (max-width: 768px) {
                            font-size: 30px;
                            top: -30px;
                        }
                    }
                }

                .cutting-card_logo {
                    display: flex;
                    align-items: center;
                    width: 100%;
                }

                .cutting-card-body {
                    margin: 16px 0;
                }

                .cutting-card-info {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    margin-bottom: 16px;
                }
            }

            @media only screen and (max-width: 768px) {
                .header-table,
                .invoice-address-table,
                .freight-collect-information {
                    table,
                    tbody,
                    td,
                    tr {
                        display: block;
                    }
                }

                .freight-collect-information .freight-collect-address-table {
                    display: table;

                    tr {
                        display: table-row;
                    }
                    tbody {
                        display: table-row-group;
                    }

                    td {
                        display: table-cell;
                    }
                }

                .invoice-address-table {
                    .section-left,
                    .section-right {
                        width: 100%;
                    }

                    .section-left {
                        padding-right: 0;
                    }
                }

                td.reprint-text {
                    text-align: left;
                }
            }
        `,
    },
};

const styles = RMADetailsViewStyles;
const mapStateToProps = (state: ApplicationStateDana) => ({
    isLoading: state.standardReturnsDetailsState.isLoading,
    language: state.context.session.language,
    logo: state.standardReturnsDetailsState.logo,
    salseOrgDetails: state.standardReturnsDetailsState.salseOrgDetails,
    orderModel: state.standardReturnsDetailsState.orderModel,
    rmaAdditionalDetails: state.standardReturnsDetailsState.rmaAdditionalDetails,
    dispositionPreferences: state.standardReturnsDetailsState.dispositionPreferences,
});

const mapDispatchToProps = {
    exportStandardReturnOrder,
};
type Props = ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;
// --- Component Definition ---
const RMADetailsView: FC<Props> = ({
    language,
    isLoading,
    orderModel,
    logo,
    salseOrgDetails,
    rmaAdditionalDetails,
    dispositionPreferences,
}) => {
    if (isLoading) {
        return <LoadingSpinner></LoadingSpinner>;
    }

    const modalContentRef = useRef<HTMLDivElement>(null);
    const [salesOrganizationDetails, setSalseOrgDetails] = useState<SalseOrgDetails>();
    const [standardReturnOrderDetails, setStandardReturnOrderDetails] = useState<StandardReturnOrderDetails>();
    const [rmaAdditionalDetail, setRmaAdditionalDetails] = useState<RmaAdditionalDetails>();
    const [dispositionPreferenceList, setDispositionPreferenceList] = useState<SystemListValuesModel[]>();

    useEffect(() => {
        setSalseOrgDetails(salseOrgDetails);
        setStandardReturnOrderDetails(orderModel);
        setRmaAdditionalDetails(rmaAdditionalDetails);
        setDispositionPreferenceList(dispositionPreferences);
    }, [salseOrgDetails, orderModel, rmaAdditionalDetails, dispositionPreferences]);

    return (
        <StyledWrapper className="invoice-container" {...styles.invoiceContainer} ref={modalContentRef}>
            <StyledWrapper className="invoice-actions text-right">
                {standardReturnOrderDetails && modalContentRef.current !== null && (
                    <InvoiceRecepients
                        invoicetype="StandardReturn"
                        invoiceData={standardReturnOrderDetails}
                        contentRef={modalContentRef}
                    />
                )}

                <PrintButton label={translate("Print Payslip")} targetRef={modalContentRef} />
            </StyledWrapper>

            {/* Header */}
            <table className="header-table">
                <tbody>
                    <tr>
                        <td className="company-logo">
                            {/* Replace src with {websiteData.companyLogoImagePath} */}
                            <img src={logo} alt="Company Logo" className="company-logo" />
                        </td>
                        <td className="company-address">
                            <address>
                                <h3>{salesOrganizationDetails?.orgName}</h3>
                                <p>
                                    {salesOrganizationDetails?.orgAddress}
                                    {(
                                        salesOrganizationDetails?.city ||
                                        salesOrganizationDetails?.state ||
                                        salesOrganizationDetails?.zipCode ||
                                        salesOrganizationDetails?.country
                                    ) && (
                                            <>
                                                <br />
                                                {[
                                                    [salesOrganizationDetails?.city, salesOrganizationDetails?.state]
                                                        .filter(Boolean)
                                                        .join(", "),
                                                    [salesOrganizationDetails?.zipCode, salesOrganizationDetails?.country]
                                                        .filter(Boolean)
                                                        .join(" "),
                                                ]
                                                    .filter(Boolean)
                                                    .join(" ")}
                                            </>
                                        )}
                                    {salesOrganizationDetails?.siteName && (
                                        <>
                                            <br />
                                            {salesOrganizationDetails.siteName}
                                        </>
                                    )}
                                </p>
                            </address>
                        </td>
                        <td className="reprint-text">
                            <Typography forwardAs="p" className="printtype-text">
                                {translate("Standard Returns")}
                            </Typography>
                            <div>{new Date().toLocaleString()}</div>
                        </td>
                    </tr>
                </tbody>
            </table>

            <table className="invoice-address-table">
                <tbody>
                    <tr>
                        <td valign="top" className="section-left">
                            {/* Sold To and Ship To */}
                            <table className="address-table">
                                <tbody>
                                    <tr>
                                        <td>
                                            <div className="invoice_card">
                                                <div className="invoice_card_header">
                                                    {" "}
                                                    {translate("Sold-To Party")}: {standardReturnOrderDetails?.billTo}
                                                </div>
                                                <div className="invoice_card_body">
                                                    <address>
                                                        <p>
                                                            {standardReturnOrderDetails?.billName}
                                                            {standardReturnOrderDetails?.billAddress1 && (
                                                                <>
                                                                    <br />
                                                                    {standardReturnOrderDetails.billAddress1}
                                                                </>
                                                            )}
                                                            {(
                                                                standardReturnOrderDetails?.billCity ||
                                                                standardReturnOrderDetails?.billState ||
                                                                standardReturnOrderDetails?.billZipcode
                                                            ) && (
                                                                    <>
                                                                        <br />
                                                                        {[
                                                                            standardReturnOrderDetails?.billCity,
                                                                            standardReturnOrderDetails?.billState,
                                                                            standardReturnOrderDetails?.billZipcode,
                                                                        ]
                                                                            .filter(Boolean)
                                                                            .join(" ")}
                                                                    </>
                                                                )}
                                                            {standardReturnOrderDetails?.billCountry && (
                                                                <>
                                                                    <br />
                                                                    {standardReturnOrderDetails.billCountry}
                                                                </>
                                                            )}
                                                        </p>
                                                    </address>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <div className="invoice_card">
                                                <div className="invoice_card_header">
                                                    {translate("Ship-from Party")}: {standardReturnOrderDetails?.shipTo}
                                                </div>
                                                <div className="invoice_card_body">
                                                    <address>
                                                        <p>
                                                            {standardReturnOrderDetails?.shipName}
                                                            {standardReturnOrderDetails?.billAddress1 && (
                                                                <>
                                                                    <br />
                                                                    {standardReturnOrderDetails.billAddress1}
                                                                </>
                                                            )}
                                                            {(
                                                                standardReturnOrderDetails?.billCity ||
                                                                standardReturnOrderDetails?.billState ||
                                                                standardReturnOrderDetails?.billZipcode
                                                            ) && (
                                                                    <>
                                                                        <br />
                                                                        {[
                                                                            standardReturnOrderDetails?.billCity,
                                                                            standardReturnOrderDetails?.billState,
                                                                            standardReturnOrderDetails?.billZipcode,
                                                                        ]
                                                                            .filter(Boolean)
                                                                            .join(" ")}
                                                                    </>
                                                                )}
                                                            {standardReturnOrderDetails?.billCountry && (
                                                                <>
                                                                    <br />
                                                                    {standardReturnOrderDetails.billCountry}
                                                                </>
                                                            )}
                                                        </p>
                                                    </address>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <div className="invoice_card">
                                                <div className="invoice_card_header">{translate("Ship Return to")}</div>
                                                <div className="invoice_card_body">
                                                    <address>
                                                        <p>
                                                            {translate(" Dana Corporation")}
                                                            {salesOrganizationDetails?.orgAddress && (
                                                                <>
                                                                    <br />
                                                                    {salesOrganizationDetails.orgAddress}
                                                                </>
                                                            )}
                                                            {(
                                                                salesOrganizationDetails?.city ||
                                                                salesOrganizationDetails?.state ||
                                                                salesOrganizationDetails?.zipCode
                                                            ) && (
                                                                    <>
                                                                        <br />
                                                                        {[
                                                                            salesOrganizationDetails?.city,
                                                                            salesOrganizationDetails?.state,
                                                                            salesOrganizationDetails?.zipCode,
                                                                        ]
                                                                            .filter(Boolean)
                                                                            .join(", ")}
                                                                    </>
                                                                )}
                                                            {salesOrganizationDetails?.country && (
                                                                <>
                                                                    <br />
                                                                    {salesOrganizationDetails.country}
                                                                </>
                                                            )}
                                                        </p>
                                                    </address>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </td>
                        <td valign="top" className="section-right">
                            {/* Delivery Information */}
                            <table className="delivery-information-table">
                                <tbody>
                                    <tr>
                                        <td>
                                            <div className="invoice_card">
                                                <div className="invoice_card_header">{translate("Information")}</div>
                                                <div className="invoice_card_body">
                                                    <table>
                                                        <tbody>
                                                            <tr>
                                                                <td className="font-semibold">
                                                                    {translate("Return Authorization No.")}
                                                                </td>
                                                                <td> {standardReturnOrderDetails?.documentNumber}</td>
                                                            </tr>
                                                            <tr>
                                                                <td className="font-semibold">
                                                                    {translate("Document Date")}
                                                                </td>
                                                                <td>
                                                                    {standardReturnOrderDetails?.documentDate
                                                                        ? getLocalizedDateTime({
                                                                            dateTime: new Date(
                                                                                standardReturnOrderDetails?.documentDate,
                                                                            ),
                                                                            language,
                                                                        })
                                                                        : ""}
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td className="font-semibold">
                                                                    {translate("Customer No.")}
                                                                </td>
                                                                <td>{standardReturnOrderDetails?.billTo}</td>
                                                            </tr>
                                                            <tr>
                                                                <td className="font-semibold">
                                                                    {translate("Currency")}
                                                                </td>
                                                                <td>{standardReturnOrderDetails?.currency}</td>
                                                            </tr>
                                                            {/* <tr>
                                                                <td className="font-semibold">
                                                                    {translate("Incoterms")}
                                                                </td>
                                                                <td></td>
                                                            </tr> */}
                                                            <tr>
                                                                <td className="font-semibold">
                                                                    {translate("Customer PO")}
                                                                </td>
                                                                <td>{standardReturnOrderDetails?.poNumber}</td>
                                                            </tr>

                                                            <tr>
                                                                <td className="font-semibold">
                                                                    {translate("Dealer Code")}
                                                                </td>
                                                                <td>{standardReturnOrderDetails?.dealerCode}</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* Reason and Disposition Preference */}

            <table className="reason-disposition-table">
                <tbody>
                    <tr>
                        <td>
                            <StyledWrapper className="border-box">
                                <Typography as="p">
                                    {translate(
                                        "If any of these parts are deemed non-returnable for credi (not on the RMA, damaged, rusted, nicks, etc.), please indicate your disposition preference.",
                                    )}
                                </Typography>
                                <ul>
                                    {dispositionPreferenceList?.map((systemList: SystemListValuesModel) => {
                                        return (
                                            <li key={systemList.id}>
                                                <strong>
                                                    {systemList.name === rmaAdditionalDetail?.disposition
                                                        ? "[✔]"
                                                        : "[ ]"}{" "}
                                                    {systemList.description}
                                                </strong>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </StyledWrapper>
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* Return Freight Collect Information */}
            {rmaAdditionalDetail?.disposition !== "" && rmaAdditionalDetail?.disposition !== "SCRAP_AT_DANA_LOCATION" && (
                <table className="freight-collect-information">
                    <tbody>
                        <tr>
                            <td>
                                <div className="invoice_card">
                                    <div className="invoice_card_header text-center">
                                        {translate("Return Freight Collect Information")}
                                    </div>
                                    <div className="invoice_card_body">
                                        <table>
                                            <tbody>
                                                <tr>
                                                    <td className="font-semibold info-title">
                                                        {translate("Shipping Freight Handling")}
                                                    </td>
                                                    <td>{standardReturnOrderDetails?.termsDescription}</td>
                                                </tr>
                                                <tr>
                                                    <td className="font-semibold info-title">
                                                        {translate("Shipping Carrier")}
                                                    </td>
                                                    <td>{standardReturnOrderDetails?.carrierName}</td>
                                                </tr>
                                                <tr>
                                                    <td className="font-semibold info-title">
                                                        {translate("Shipping Carrier Account")}
                                                    </td>
                                                    <td>{rmaAdditionalDetail?.carrierAccountNo}</td>
                                                </tr>
                                                <tr>
                                                    <td className="font-semibold info-title">
                                                        {translate("Return Reference Number")}
                                                    </td>
                                                    <td>{rmaAdditionalDetail?.internalRefNumber}</td>
                                                </tr>
                                                <tr>
                                                    <td className="font-semibold info-title">
                                                        {translate("Shipping Instructions")}
                                                    </td>
                                                    <td>{rmaAdditionalDetail?.shippingInstructions}</td>
                                                </tr>
                                                <tr>
                                                    <td></td>
                                                    <td></td>
                                                </tr>
                                            </tbody>
                                        </table>

                                        <table className="freight-collect-address-table">
                                            <thead>
                                                <tr>
                                                    <th className="text-left">{translate("Address")}</th>
                                                    <th className="text-left">{translate("Contact")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>
                                                        <address>
                                                            <td>{rmaAdditionalDetail?.customerName}</td>
                                                            {rmaAdditionalDetail?.address1 && (
                                                                <>
                                                                    <br />
                                                                    <td>{rmaAdditionalDetail.address1}</td>
                                                                </>
                                                            )}
                                                            {(rmaAdditionalDetail?.city || rmaAdditionalDetail?.state) && (
                                                                <>
                                                                    <br />
                                                                    {[rmaAdditionalDetail?.city, rmaAdditionalDetail?.state]
                                                                        .filter(Boolean)
                                                                        .join(" ")}
                                                                </>
                                                            )}
                                                        </address>
                                                    </td>
                                                    <td>
                                                        {rmaAdditionalDetail?.firstName} {rmaAdditionalDetail?.lastName}
                                                        <br />
                                                        {rmaAdditionalDetail?.phone}
                                                        <br />
                                                        {rmaAdditionalDetail?.email}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            )}
            {/* Line Items table */}
            <StyledWrapper className="line-item-table-wrapper">
                <StyledTable className="line-item-table">
                    <thead>
                        <tr>
                            <th>{translate("Item")}</th>
                            <th>{translate("Material/Description")}</th>
                            <th>{translate("Quantity/UOM")}</th>
                            <th>{translate(`Price (${standardReturnOrderDetails?.currency})`)}</th>
                            <th>{translate(`Unit Core Deposit (${standardReturnOrderDetails?.currency})`)}</th>
                            <th>{translate(`Extended Amount (${standardReturnOrderDetails?.currency})`)}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {standardReturnOrderDetails?.orderInfo.map((orderline: OrderInfo) => {
                            return (
                                <tr key={orderline.lineNumber}>
                                    <td>{orderline.lineNumber}</td>
                                    <td className="break-spaces">
                                        <div>
                                            {orderline.partNumber}
                                            <br />
                                            {orderline.partDescription}
                                            <br />
                                            {translate("Customer Part No")}: {orderline.customerItemNumber}
                                            <br />
                                            {translate("Country Of Origin")}: {orderline.countryofOrigin}
                                            <br />
                                            {translate("Custom Commodity Code")}: {orderline.hsCode}
                                            <br />
                                        </div>
                                    </td>
                                    <td> {orderline.quantityOrdered} EA</td>
                                    <td className="price-cell">{formatDecimal(orderline.baseprice)} </td>
                                    <td className="price-cell">{formatDecimal(orderline.extendedAmount)}</td>
                                    <td className="price-cell">
                                        {formatDecimal(
                                            orderline.baseprice * orderline.quantityOrdered +
                                            orderline.extendedAmount
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>

                    {(() => {
                        if (!standardReturnOrderDetails?.orderInfo) {
                            return null;
                        }

                        const restockingFees = standardReturnOrderDetails?.orderInfo.reduce(
                            (sum, item) => sum + (item.restockingFees || 0),
                            0,
                        );
                        const totalNetAmount = standardReturnOrderDetails?.orderInfo.reduce(
                            (sum, item) => sum + (item.baseprice * item.quantityOrdered + item.extendedAmount || 0),
                            0,
                        );
                        const finalAmount = totalNetAmount - restockingFees;

                        return (
                            <tfoot>
                                <tr>
                                    <td colSpan={4}></td>
                                    <td colSpan={2} rowSpan={2}>
                                        <div className="pricing-box">
                                            <div>
                                                <strong>{translate("Items Total:")}</strong>
                                                <p>{formatDecimal(totalNetAmount)}</p>
                                            </div>
                                            <div>
                                                <strong>{translate("Re-stocking Fees:")}</strong>
                                                <p>{formatDecimal(restockingFees)}</p>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                <tr></tr>
                                <tr></tr>
                                <tr>
                                    <td colSpan={4}></td>
                                    <td colSpan={1}>
                                        <strong>{translate("Final Amount:")}</strong>
                                    </td>
                                    <td className="price-cell">{formatDecimal(finalAmount)}</td>
                                </tr>
                            </tfoot>
                        );
                    })()}
                </StyledTable>
            </StyledWrapper>

            {/* Cutting label sections */}
            <StyledWrapper className="cutting-card-wrapper first-card">
                <Typography>
                    {translate("1. Cut this label and affix to the outside of your return packaging")}
                </Typography>
                <StyledWrapper className="cutting-card">
                    <StyledWrapper className="cutting-card_logo">
                        <img src={logo} alt="" />
                        <Typography variant="h2" className="font-semibold">
                            {translate("Standard Returns")}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper className="cutting-card-body">
                        <StyledWrapper className="cutting-card-info">
                            <Typography>{translate("Return Authorization No:")}</Typography>
                            <Typography> {standardReturnOrderDetails?.documentNumber}</Typography>
                        </StyledWrapper>
                        <StyledWrapper className="cutting-card-info">
                            <Typography>{translate("Customer Name:")}</Typography>
                            <Typography> {standardReturnOrderDetails?.billName}</Typography>
                        </StyledWrapper>
                        <StyledWrapper className="cutting-card-info">
                            <Typography>{translate("Customer No:")}</Typography>
                            <Typography> {standardReturnOrderDetails?.billTo}</Typography>
                        </StyledWrapper>
                        <StyledWrapper className="cutting-card-info">
                            <Typography>{translate("Return Reference No:")}</Typography>
                            <Typography> {standardReturnOrderDetails?.processNumber}</Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>

            <StyledWrapper className="cutting-card-wrapper">
                <Typography>{translate("2. Place this label inside your return packaging")}</Typography>
                <StyledWrapper className="cutting-card">
                    <StyledWrapper className="cutting-card_logo">
                        <img src={logo} alt="" />
                        <Typography variant="h2" className="font-semibold">
                            {translate("Standard Returns")}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper className="cutting-card-body">
                        <StyledWrapper className="cutting-card-info">
                            <Typography>{translate("Return Authorization No:")}</Typography>
                            <Typography> {standardReturnOrderDetails?.documentNumber}</Typography>
                        </StyledWrapper>
                        <StyledWrapper className="cutting-card-info">
                            <Typography>{translate("Customer Name:")}</Typography>
                            <Typography> {standardReturnOrderDetails?.billName}</Typography>
                        </StyledWrapper>
                        <StyledWrapper className="cutting-card-info">
                            <Typography>{translate("Customer No:")}</Typography>
                            <Typography> {standardReturnOrderDetails?.billTo}</Typography>
                        </StyledWrapper>
                        <StyledWrapper className="cutting-card-info">
                            <Typography>{translate("Return Reference No:")}</Typography>
                            <Typography> {standardReturnOrderDetails?.processNumber}</Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(RMADetailsView);
